import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Download, Send, CheckCircle2, Trash2, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { invoicesAPI, downloadFile } from '../../services/api';
import { useCompany } from '../../services/companyState';
import { unitLabel, formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import './InvoiceDetail.css';

const STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Versendet',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
};
const STATUS_CLASS = {
  draft: 'status-badge status-draft',
  sent: 'status-badge status-sent',
  paid: 'status-badge status-paid',
  overdue: 'status-badge status-overdue',
};

const formatDE = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await invoicesAPI.getById(id);
      setInvoice(data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Rechnung nicht gefunden' : 'Fehler beim Laden');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDownloadPdf = async () => {
    try {
      setBusy(true);
      if (!invoice.pdf_path) {
        await invoicesAPI.generatePDF(id);
      }
      const res = await invoicesAPI.download(id);
      downloadFile(res.data, `Rechnung_${invoice.invoice_number}.pdf`);
    } catch (err) {
      toast.error('Fehler beim PDF-Download');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setBusy(true);
      await invoicesAPI.generatePDF(id);
      toast.success('PDF erstellt');
      load();
    } catch (err) {
      toast.error('PDF-Erstellung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm('Rechnung als bezahlt markieren?')) return;
    try {
      setBusy(true);
      await invoicesAPI.markPaid(id);
      toast.success('Als bezahlt markiert');
      load();
    } catch (err) {
      toast.error('Fehler');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!invoice.customer?.email) {
      toast.error('Kunde hat keine E-Mail-Adresse');
      return;
    }
    if (!window.confirm(`Rechnung an ${invoice.customer.email} senden?`)) return;
    try {
      setBusy(true);
      await invoicesAPI.send(id);
      toast.success('Rechnung wird versendet');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Rechnung ${invoice.invoice_number} wirklich löschen?`)) return;
    try {
      setBusy(true);
      await invoicesAPI.delete(id);
      toast.success('Gelöscht');
      navigate('/invoices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (!invoice) return null;

  const items = (invoice.items || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div>
      <div className="content-header">
        <div>
          <Link to="/invoices" className="back-link">
            <ArrowLeft size={14} />
            Zurück zur Übersicht
          </Link>
          <h1>
            Rechnung {invoice.invoice_number}{' '}
            <span className={STATUS_CLASS[invoice.status]} style={{ fontSize: '0.7em', verticalAlign: 'middle', marginLeft: '0.5rem' }}>
              {STATUS_LABELS[invoice.status] || invoice.status}
            </span>
          </h1>
          {invoice.title && <p>{invoice.title}</p>}
        </div>
        <div className="flex gap-2">
          {invoice.status === 'draft' && (
            <Link to={`/invoices/${id}/edit`} className="btn btn-outline">
              <Edit2 size={14} />
              Bearbeiten
            </Link>
          )}
          <button onClick={handleDownloadPdf} className="btn btn-outline" disabled={busy}>
            <Download size={14} />
            PDF
          </button>
          {!invoice.pdf_path && (
            <button onClick={handleGeneratePdf} className="btn btn-outline" disabled={busy}>
              <FileText size={14} />
              PDF erzeugen
            </button>
          )}
          {invoice.status === 'draft' && invoice.customer?.email && (
            <button onClick={handleSend} className="btn btn-primary" disabled={busy}>
              <Send size={14} />
              Versenden
            </button>
          )}
          {invoice.status !== 'paid' && (
            <button onClick={handleMarkPaid} className="btn btn-primary" disabled={busy}>
              <CheckCircle2 size={14} />
              Bezahlt
            </button>
          )}
          {invoice.status === 'draft' && (
            <button onClick={handleDelete} className="btn btn-danger" disabled={busy}>
              <Trash2 size={14} />
              Löschen
            </button>
          )}
        </div>
      </div>

      <div className="content-body">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card">
            <div className="card-header"><h2>Rechnungsempfänger</h2></div>
            <div className="card-body">
              <div className="font-semibold">{invoice.customer?.name}</div>
              <div>{invoice.customer?.address_line1}</div>
              {invoice.customer?.address_line2 && <div>{invoice.customer.address_line2}</div>}
              <div>{invoice.customer?.postal_code} {invoice.customer?.city}</div>
              <div>{invoice.customer?.country}</div>
              {invoice.customer?.email && (
                <div className="text-sm text-gray-600 mt-2">{invoice.customer.email}</div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2>Rechnungsdaten</h2></div>
            <div className="card-body">
              <dl className="invoice-meta">
                <dt>Rechnungsnummer</dt><dd>{invoice.invoice_number}</dd>
                <dt>Rechnungsdatum</dt><dd>{formatDE(invoice.invoice_date)}</dd>
                <dt>Leistungsdatum</dt><dd>{formatDE(invoice.service_date)}</dd>
                <dt>Fälligkeit</dt><dd>{formatDE(invoice.due_date)}</dd>
                {invoice.sent_at && (<><dt>Versendet am</dt><dd>{new Date(invoice.sent_at).toLocaleString('de-DE')}</dd></>)}
                {invoice.paid_at && (<><dt>Bezahlt am</dt><dd>{new Date(invoice.paid_at).toLocaleString('de-DE')}</dd></>)}
              </dl>
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <div className="card-header"><h2>Positionen</h2></div>
          <div className="card-body">
            <table className="table">
              <thead>
                <tr>
                  <th>Pos.</th>
                  <th>Beschreibung</th>
                  <th className="text-right">Menge</th>
                  <th>Einheit</th>
                  <th className="text-right">Einzelpreis</th>
                  <th className="text-right">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id}>
                    <td>{idx + 1}</td>
                    <td>{it.description}</td>
                    <td className="text-right">{Number(it.quantity).toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                    <td>{unitLabel(it.unit)}</td>
                    <td className="text-right">{formatEuro(it.unit_price)}</td>
                    <td className="text-right font-semibold">{formatEuro(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-right font-semibold">Nettobetrag</td>
                  <td className="text-right font-semibold">{formatEuro(invoice.net_amount)}</td>
                </tr>
                {isKleinunternehmer ? (
                  <tr>
                    <td colSpan={5} className="text-right">Umsatzsteuer (§19 UStG)</td>
                    <td className="text-right">0,00 €</td>
                  </tr>
                ) : (
                  Object.entries(invoice.vat_breakdown || {}).length > 0 ? (
                    Object.entries(invoice.vat_breakdown).map(([rate, amounts]) => (
                      <tr key={rate}>
                        <td colSpan={5} className="text-right">
                          Umsatzsteuer {Number(rate).toFixed(0)}% (auf {formatEuro(amounts.net)})
                        </td>
                        <td className="text-right">{formatEuro(amounts.vat)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-right">Umsatzsteuer</td>
                      <td className="text-right">{formatEuro(invoice.vat_amount || 0)}</td>
                    </tr>
                  )
                )}
                <tr>
                  <td colSpan={5} className="text-right font-semibold" style={{ fontSize: '1.05rem' }}>Rechnungsbetrag</td>
                  <td className="text-right font-semibold" style={{ fontSize: '1.05rem', color: '#1d4ed8' }}>
                    {formatEuro(isKleinunternehmer ? invoice.net_amount : (invoice.gross_amount || invoice.net_amount))}
                  </td>
                </tr>
              </tfoot>
            </table>
            {isKleinunternehmer && (
              <div className="kleinunternehmer-notice" style={{ marginTop: '0.75rem' }}>
                Gemäß §19 UStG (Kleinunternehmer) wird keine Umsatzsteuer berechnet.
              </div>
            )}
          </div>
        </div>

        {invoice.notes && (
          <div className="card">
            <div className="card-header"><h2>Anmerkungen</h2></div>
            <div className="card-body" style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;
